import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Diálogo modal — o primeiro do design system.
//
// POR QUE ELE EXISTE. O padrão já estava escrito à mão em quatro lugares
// (`ConfirmDeleteModal`, `ConfirmarRecusaModal`, `TwoFactorModal`,
// `servidores/_editModal`), e as cópias DIVERGIRAM: o `zIndex` vale 100, 110 ou
// 200 conforme o arquivo, e o scrim tem uma cor diferente em cada um. Cada cópia
// também refaz o `useEffect` de ESC por conta própria — e nenhuma devolve o foco
// ao gatilho quando fecha.
//
// A quinta cópia seria escrita pela tela de trava de margem, que precisa de três
// overlays com requisito explícito de foco e teclado. Em vez disso, o padrão
// passa a morar aqui, com UM scrim e UM `zIndex`.
//
// Migrar os quatro existentes não é escopo de quem criou este arquivo — eles
// migram quando forem tocados. Mas nada novo deve nascer fora daqui.
import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
/**
 * Uma camada só, para todo o produto.
 *
 * Ficar acima do `AppShellAdmin` e de qualquer sticky é o requisito; competir
 * com outro modal não é, porque não há caso de dois abertos ao mesmo tempo.
 */
const CAMADA_MODAL = 200;
// `xl` existe para a ficha só-leitura do servidor, que põe lado a lado uma grade
// de cadastro, um quadro de margem por modalidade e a tabela de contratos. Em
// `lg` essa tabela rolava na horizontal, e rolagem lateral dentro de diálogo
// esconde coluna sem avisar que ela existe.
const LARGURAS = { sm: 420, md: 560, lg: 820, xl: 1180 };
/** Tudo que pode receber foco dentro do diálogo, na ordem em que o Tab anda. */
const FOCAVEIS = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
export function Modal({ open, onClose, title, children, footer, size = "md", danger = false, eyebrow, subtitle, showClose = false, }) {
    const caixa = useRef(null);
    // Quem tinha o foco quando o diálogo abriu. Sem guardar, fechar joga o foco
    // no `<body>` e quem navega por teclado recomeça do topo da página.
    const gatilho = useRef(null);
    const tituloId = useId();
    // O `onClose` mais recente, sem entrar nas dependencias do efeito. Quem usa o
    // Modal costuma passar `onClose={() => setAberto(false)}`, uma funcao nova a
    // cada render; com ela nas dependencias, cada tecla digitada num campo rodava
    // o efeito de novo — devolvia o foco ao gatilho e o jogava no PRIMEIRO campo.
    // O segundo campo (um textarea de justificativa, por exemplo) perdia o foco a
    // cada letra.
    const aoFechar = useRef(onClose);
    aoFechar.current = onClose;
    useEffect(() => {
        if (!open)
            return;
        gatilho.current = document.activeElement;
        // O foco entra no diálogo. Primeiro controle focável; se não houver
        // nenhum, a própria caixa (daí o `tabIndex={-1}` nela).
        const primeiro = caixa.current?.querySelector(FOCAVEIS);
        (primeiro ?? caixa.current)?.focus();
        function aoTeclar(e) {
            if (e.key === "Escape") {
                e.stopPropagation();
                aoFechar.current();
                return;
            }
            if (e.key !== "Tab")
                return;
            // Foco preso: sem isto o Tab sai do diálogo e passeia pela página
            // atrás do scrim, onde o usuário não vê onde está.
            const alvos = [...(caixa.current?.querySelectorAll(FOCAVEIS) ?? [])];
            if (alvos.length === 0)
                return;
            const primeiroAlvo = alvos[0];
            const ultimo = alvos[alvos.length - 1];
            if (!e.shiftKey && document.activeElement === ultimo) {
                e.preventDefault();
                primeiroAlvo.focus();
            }
            else if (e.shiftKey && document.activeElement === primeiroAlvo) {
                e.preventDefault();
                ultimo.focus();
            }
        }
        document.addEventListener("keydown", aoTeclar, true);
        return () => {
            document.removeEventListener("keydown", aoTeclar, true);
            // Devolve o foco a quem abriu. Roda na limpeza, e não num `onClose`
            // envolvido, porque o diálogo também fecha por caminhos que não passam
            // pelo botão — ESC e clique no scrim.
            gatilho.current?.focus?.();
        };
        // So' abrir/fechar muda o foco. Trocar o `onClose` a cada render nao pode.
    }, [open]);
    if (!open || typeof document === "undefined")
        return null;
    // O portal e parte do posicionamento, nao apenas uma conveniencia. Cartoes do
    // produto usam `backdrop-filter`, que cria um containing block para filhos
    // `position: fixed`. Sem sair dessa arvore, o scrim fica preso e recortado ao
    // cartao onde o botao foi renderizado, em vez de cobrir a viewport.
    return createPortal(_jsx("div", { 
        // Clicar fora fecha. Só no próprio scrim: `currentTarget === target`
        // impede que um clique que começou dentro e terminou aqui feche a caixa.
        onMouseDown: (e) => {
            if (e.currentTarget === e.target)
                onClose();
        }, style: {
            position: "fixed",
            inset: 0,
            background: "var(--scrim)",
            display: "grid",
            placeItems: "center",
            zIndex: CAMADA_MODAL,
            // Em telas estreitas 24px de cada lado comem largura que o conteúdo
            // precisa; `clamp` devolve essa margem sem apertar no desktop.
            padding: "clamp(12px, 3vw, 24px)",
            overflowY: "auto",
        }, children: _jsxs("div", { ref: caixa, role: "dialog", "aria-modal": "true", "aria-labelledby": tituloId, tabIndex: -1, style: {
                width: "100%",
                maxWidth: LARGURAS[size],
                background: "var(--surface-solid)",
                border: `1px solid ${danger ? "var(--danger-500)" : "var(--border-strong)"}`,
                borderRadius: "var(--radius-lg)",
                boxShadow: "var(--shadow-lg)",
                padding: "clamp(16px, 2.4vw, 24px)",
                // O DIÁLOGO NÃO PASSA DA TELA, e é o CORPO que rola.
                //
                // Antes a caixa crescia com o conteúdo e o scrim rolava. Mas o scrim
                // CENTRALIZA o diálogo, e conteúdo centralizado que transborda deixa o
                // TOPO inalcançável: a área rolável não inclui o que ficou acima da
                // borda. Numa janela baixa isso escondia o título e o × junto.
                //
                // `dvh` e não `vh`: no mobile a barra do navegador entra e sai, e `vh`
                // congela na altura MAIOR — a caixa passava da tela justamente quando a
                // barra estava visível.
                maxHeight: "calc(100dvh - clamp(24px, 6vw, 48px))",
                display: "flex",
                flexDirection: "column",
                gap: 16,
                outline: "none",
            }, children: [_jsxs("div", { style: { display: "flex", alignItems: "flex-start", gap: 16, justifyContent: "space-between", flexShrink: 0 }, children: [_jsxs("div", { style: { minWidth: 0 }, children: [eyebrow ? (_jsx("div", { style: { fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 6 }, children: eyebrow })) : null, _jsx("h2", { id: tituloId, style: { margin: 0, fontSize: eyebrow ? 26 : 18, lineHeight: 1.2, color: danger ? "var(--danger-500)" : "var(--text)" }, children: title }), subtitle ? (_jsx("div", { style: { marginTop: 6, fontSize: 14, color: "var(--text-muted)" }, children: subtitle })) : null] }), showClose ? (_jsx("button", { type: "button", "aria-label": "Fechar", onClick: onClose, style: {
                                flexShrink: 0, width: 34, height: 34, borderRadius: "var(--radius-pill)",
                                border: "1px solid var(--border-strong)", background: "var(--bg-elev-2)",
                                color: "var(--text-muted)", cursor: "pointer", fontSize: 17, lineHeight: 1,
                                display: "grid", placeItems: "center",
                            }, children: "\u00D7" })) : null] }), _jsx("div", { style: { display: "grid", gap: 12, flex: "1 1 auto", minHeight: 0, overflowY: "auto", overflowX: "hidden" }, children: children }), footer && _jsx("div", { style: { display: "flex", gap: 8, justifyContent: "flex-end", flexShrink: 0 }, children: footer })] }) }), document.body);
}
//# sourceMappingURL=Modal.js.map